<template>
  <div class="max-w-7xl mx-auto p-4 lg:p-8 bg-gray-50" :class="courseDataObject.loading ? '' : 'min-h-screen'">
    <div v-if="courseDataObject.loading" class="flex justify-center my-10">
      <LoaderComponent />
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8" v-else>
      <div class="lg:col-span-8 flex flex-col gap-6">
        <div class="bg-white border border-iron shadow-sm overflow-hidden rounded-sm">
          <div class="relative bg-gray-100 aspect-[16/11.9] flex items-center justify-center">
            <img 
              v-if="hasCertificate()"
              :src="getCertificateSvg()" 
              :alt="getCertificateSvgAlt()"
              class="w-full h-auto object-contain"
            />
            <div v-else class="flex flex-col items-center opacity-1">
              <img src="@/assets/logo.webp" class="h-12 mb-2" alt="Logo Placeholder">
              <span class="text-gray-500 font-medium uppercase tracking-widest text-xs">
                {{ translate('Certificate Pending') }}
              </span>
              <i class="pi pi-lock mt-2 text-gray-400"></i>
            </div>
            
          </div>
          
          <div class="p-6 text-gray-700 text-sm leading-relaxed border-t border-iron">
            <p v-if="hasCertificate()">
              {{ translate('The certificate above attests that') }} <strong class="text-black">{{ courseDataObject.data.certificate.user_name }}</strong> 
              {{ translate('successfully completed the course') }} <strong class="text-black">{{ courseDataObject.data.certificate.course_title }}</strong> 
              {{ translate('at') }} {{ courseDataObject.data.certificate.getFinishedAtDate() }}, {{ translate('taught by') }} 
              <span class="text-blue-600">
                {{ courseDataObject.data.certificate.instructor_names }}
              </span>. 
              {{ translate('The certificate indicates that the entire course has been completed, as validated by the student.') }}
              {{ translate('The course duration represents the total hours of video and article lectures at the time of the most recent completion.') }}
            </p>

            <p v-else>
              {{ translate('Your certificate does not yet exist or is in the process of being generated. Please wait a few hours while we generate your certificate.') }}
            </p>
          </div>
        </div>
      </div>
      
      <div class="lg:col-span-4 flex flex-col gap-8">
        
        <section v-if="hasCertificateAndUserName()">
          <h2 class="text-xl font-bold mb-4 text-gray-900">
            {{ translate('Recipient of the certificate:') }}
          </h2>
          <div class="flex items-center gap-4">
            <Avatar 
              :label="getInitials(courseDataObject.data.certificate.user_name)" 
              size="large" 
              shape="circle" 
              class="bg-gray-900 text-white font-bold"
            />
            <div>
              <router-link to="#" class="text-lg font-bold text-gray-900 hover:text-blue-600">
                {{ courseDataObject.data.certificate ? courseDataObject.data.certificate.user_name : '' }}
              </router-link>
            </div>
          </div>
        </section>

        <section>
          <h2 class="text-xl font-bold mb-4 text-gray-900">
            {{ translate('About the course:') }}
          </h2>
          <div 
            class="bg-white border border-iron p-3 rounded-sm flex gap-4 hover:bg-gray-50 cursor-pointer transition-colors"
            @click.prevent="urlHandler.redirectAndScroll(`/course/${courseDataObject.data.content.uuid}`)"
          >
            <img 
              :src="courseDataObject.data.content.getCoursePhoto()" 
              class="w-24 h-14 object-cover rounded-sm shadow-inner rounded"
            />
            <div class="flex-1">
              <h3 class="font-bold text-sm text-gray-900 leading-tight mb-1">
                {{ courseDataObject.data.content.title }}
              </h3>
              <p class="text-xs text-gray-600 mb-1">{{ InstructorDataHandler.getInformation('name') }}</p>
              
              <div class="flex items-center gap-2 mb-1" v-if="1 != 1">
                <span class="text-sm font-bold text-yellow-700">{{ courseDataObject.data.content.getRating() }}</span>
                <Rating v-model="courseDataObject.data.content.rating" readonly :cancel="false" size="small" />
                <span class="text-xs text-gray-500">({{ courseDataObject.data.statistics.reviewsCount }})</span>
              </div>
              
              <div class="text-xs text-gray-500">
                {{ courseDataObject.data.content.getCourseTimeInHours() }} 
                {{ courseDataObject.data.content.getTimeInMinutes() < 60 ? translate('minutes') : translate('hours') }} • 
                {{ courseDataObject.data.statistics.videosNumber }} {{ translate('classes') }}
              </div>
            </div>
          </div>
        </section>

        <section class="flex flex-col gap-3" v-if="hasCertificate()">
          <div class="relative">
            <Button 
              :label="translate('download', 'ucfirst')" 
              icon="pi pi-download" 
              class="bg-purple-suit-forced w-full hover:opacity-75 transition" 
              @click="downloadCertificate('pdf')"
            />
            <!-- <TieredMenu ref="downloadMenu" :model="downloadOptions" popup /> -->
          </div>

          <!-- <Button 
            :label="translate('share', 'ucfirst')" 
            icon="pi pi-share-alt" 
            class="no-bg-button w-full hover:opacity-75 transition" 
            @click="openSharer()"
          /> -->

          <Button 
            v-if="authStore.isAuthenticated && courseDataObject.data.certificate.isMyCertificate(authStore.getUser.uuid)"
            :label="translate('update name on certificate', 'ucfirst')" 
            icon="pi pi-pencil" 
            class="bg-button w-full hover:opacity-75 transition" 
            @click="openNewCertifcateNameChangeDialog()"
          />
        </section>

      </div>
    </div>

    <!-- <Dialog v-model:visible="sharerData.visible" modal :closable="true" :draggable="false"
      :header="translate('share this course', 'ucfirst')" :style="{ width: '35rem' }" class="ml-2 me-2">
      <SharerComponent :link="sharerData.link" :title="sharerData.title" :description="sharerData.description" :socialMediasSharing="['whatsapp','facebook']" />
    </Dialog> -->

    <Dialog v-model:visible="certificateChangeData.visible" modal :closable="true" :draggable="false"
      :header="translate('change certificate name', 'ucfirst')" :style="{ width: '35rem' }" class="ml-2 me-2">
        <div class="px-2">
          <template v-if="certificateChangeData.allowed">
            <p class="font-medium">
              {{ translate('if you confirm the new name change request, your certificate will be unavaliable for some minutes', 'ucfirst') }}.
            </p>
            <div class="my-3 flex justify-between flex-wrap md:flex-nowrap">
              <InputText 
                type="text"
                v-model="certificateChangeData.certificateName"
                class="w-full md:mr-3"
              />
              <Button 
                :label="translate('confirm', 'ucfirst')"
                class="w-full mt-1 md:mt-0 md:w-1/6 no-bg-button"
                :disabled="!certificateChangeData.certificateName"
                @click="requestCertificateNameChange()"
                :loading="certificateChangeData.loading"
              />
            </div>
          </template>
          <div v-else class="font-medium">
            <p>{{ certificateChangeData.infoMessageDescription }}</p>
            <small>{{ certificateChangeData.infoMessageRemainingTime }}.</small>
          </div>
        </div>
    </Dialog>
  </div>
</template>

<script setup>
import { onMounted, reactive } from 'vue';
import Avatar from 'primevue/avatar';
import Rating from 'primevue/rating';
import Button from 'primevue/button';
// import TieredMenu from 'primevue/tieredmenu';
import courseService from '@/services/CourseService';
import { useRoute } from 'vue-router';
import { translate } from '@/composables/translate';
import urlHandler from '@/composables/urlHandler';
import { useSessionMessageStore } from '@/stores/sessionMessage';
import LoaderComponent from '@/components/Utils/LoaderComponent.vue';
import InstructorDataHandler from '@/composables/handlers/InstructorDataHandler';
import UserCourseService from '@/services/UserCourseService'
import { showToast } from '@/composables/toast';
import { Dialog, useToast } from 'primevue';
// import SharerComponent from '@/components/misc/SharerComponent.vue';
// import { minutesToTime } from '@/composables/locales';
// import { env } from '@/composables/config';
import { getInitials } from '@/composables/textUtils';
import CertificateService from '@/services/CertificateService';
import { useAuthStore } from '@/stores/auth';
import InputText from 'primevue/inputtext';

const toast = useToast();
const authStore = useAuthStore();
const sessionMessage = useSessionMessageStore();
const route = useRoute();
const courseDataObject = reactive({
  uuid: null,
  data: {
    content: null,
    statistics: null
  },
  loading: true,
  certificate: null
});
const certificateChangeData = reactive({
  visible: false,
  certificateName: null,
  allowed: false,
  infoMessageDescription: null,
  infoMessageRemainingTime: null
});

onMounted(() => {
  fetchData();
});

/**
 * Gets Certificate information
 */
async function fetchData() {
  const type = route.query?.type || 'certificate';
  const currentUuid = route.params?.uuid;

  courseDataObject.uuid = currentUuid;

  if (!currentUuid) {
    return goToDashboard(translate('course not found', 'ucfirst'));
  }

  if (type == 'certificate') {
    courseDataObject.data.certificate = await CertificateService.find(currentUuid);
    if (courseDataObject.data.certificate) {
      courseDataObject.uuid = courseDataObject.data.certificate.course_uuid;
    }

  } else {
    courseDataObject.data.certificate = await UserCourseService.getUserCourseCertificate(courseDataObject.uuid);

  }

  if (!courseDataObject.data.certificate || courseDataObject.data.certificate == null) {
    return goToDashboard(translate('certificate not found', 'ucfirst'));
  }

  await getCourseData();

  courseDataObject.loading = false;
}

/**
 * Gets Course data
 */
async function getCourseData() {
  const storageData = courseService.getStoragedCourseData(courseDataObject.uuid);

  if (
    !storageData || 
    (storageData.data == undefined || storageData.statistics == undefined) 
  ) {
    const response = await courseService.showCourse(courseDataObject.uuid);
    if (!response.success()) {
      return goToDashboard(translate('course information not found', 'ucfirst'));
    }
    courseDataObject.data.content = response.getContent();

    const courseStatistics = await courseService.getStatistics(courseDataObject.uuid, true);
    if (courseStatistics.success()) {
      courseDataObject.data.statistics = courseStatistics.getContent().getStatisticData();
    }

  } else {
    courseDataObject.data.content = storageData.content; 
    courseDataObject.data.statistics = storageData.statistics;
  }
}

/**
 * Redirects to dashborad if logged in or to homepage if not
 * @param errorMessage 
 */
async function goToDashboard(errorMessage = null) {
  if (errorMessage) {
    sessionMessage.errorMessage(errorMessage);
  }

  const routeToGo = authStore.isAuthenticated ? '/dashboard/' : '/'; 
  await urlHandler.redirectAndScroll(routeToGo);

  courseDataObject.loading = false;
  return false;
}

async function downloadCertificate(format = 'pdf') {
  if (!hasCertificate() || !courseDataObject.data.certificate.hasFile(format)) {
    showToast(toast, translate('selected certificate format not avaliable', 'ucfirst'), 'error');
    return;
  }

  const fileSourceBlob = courseDataObject.data.certificate.getFile(format);
  const binaryData = fileSourceBlob.data ? fileSourceBlob.data : fileSourceBlob;

  const uint8Array = new Uint8Array(binaryData);
  const blob = new Blob([uint8Array], { 
    type: format === 'pdf' ? 'application/pdf' : 'image/svg+xml' 
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', courseDataObject.data.certificate.generateFileName(format));

  document.body.appendChild(link);
  link.click();

  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Requests Certificate name change
 */
async function requestCertificateNameChange() {
  if (!certificateChangeData.certificateName) {
    showToast(toast, translate('a name is required', 'ucfirst'), 'error');
    return;
  }

  if (certificateChangeData.certificateName == courseDataObject.data.certificate.user_name) {
    showToast(toast, translate('the name must be different', 'ucfirst'), 'error');
    return;
  }

  certificateChangeData.loading = true;

  const response = await UserCourseService.requestCertificateNameChange(
    courseDataObject.data.certificate.uuid, certificateChangeData.certificateName
  );

  certificateChangeData.loading = false;

  if (!response.success()) {
    showToast(toast, translate('a problem occurred, try again later', 'ucfirst'), 'error');
    return;
  }

  certificateChangeData.visible = false;
  certificateChangeData.certificateName = null;

  sessionMessage.successMessage(translate('certificate name changed', 'ucfirst'))
  urlHandler.reloadPage();
}

/**
 * Opens the change Certificate Name dialog and perform checks if user can change the name
 */
function openNewCertifcateNameChangeDialog() {
  certificateChangeData.allowed = true;

  if (!courseDataObject.data.certificate.canChangeCertificateName()) {
    certificateChangeData.allowed = false;
    certificateChangeData.infoMessageDescription = translate('you have already changed the name recently, please wait some time before requesting another change', 'ucfirst');
    certificateChangeData.infoMessageRemainingTime = translate('you must wait', 'ucfirst') + ' ' + courseDataObject.data.certificate.getRemainingTimeToAllowChangeNameRequest();
  }

  certificateChangeData.visible = !certificateChangeData.visible;
}

/**
 * Checks if Certificate exists
 */
function hasCertificate() {
  return (
    courseDataObject.data.certificate.isValid() &&
    courseDataObject.data.certificate != null && 
    courseDataObject.data.certificate.certificate_assets.hasFiles()
  );
}

/**
 * Gets certificate jpeg or ''
 */
function getCertificateSvg() {
  return hasCertificate()
    ? courseDataObject.data.certificate.getSvgFile(false)
    : '';
}

/**
 * Gets certificate jpeg alt
 */
function getCertificateSvgAlt() {
  return hasCertificate() 
    ? translate('certificate of completion', 'ucfirst') 
    : translate('certificate not found', 'ucfirst');
}

// const downloadMenu = ref();
// const downloadOptions = ref([
  // { label: '.svg', icon: 'pi pi-image', command: () => downloadCertificate('svg') },
  // { label: '.pdf', icon: 'pi pi-file-pdf', command: () => downloadCertificate('pdf') }
// ]);

// const toggleDownloadMenu = (event) => {
//   downloadMenu.value.toggle(event);
// };

function hasCertificateAndUserName() {
  return courseDataObject.data.certificate && courseDataObject.data.certificate.user_name;
}

// let sharerData = ref({
//   visible: false,
//   link: '',
//   title: '',
//   description: '',
//   hashtags: ''
// });
// function openSharer() {
//   if ([null, undefined, ''].includes(courseDataObject.data.content)) {
//     return;
//   }

//   let textToShare = `${translate('Check out the Course:')} ${courseDataObject.data.content.title}! \n\n`;
//   textToShare += `${translate('What will you find?')}\n`;
//   textToShare += `${courseDataObject.data.content.description}\n\n`;
//   textToShare += `${translate('duration', 'ucfirst')}: ${courseDataObject.data.content.timeInMinutes} minutos\n`;
//   textToShare += `${translate('price', 'ucfirst')}: R$ ${minutesToTime(courseDataObject.data.content.price)}\n\n`;
//   textToShare += `${translate('Share with your friends and come learn together!')} \n\n`;
//   textToShare += `${translate('Find out more at:')} ${env('APP_BASE_URL')}course/${courseDataObject.data.content.uuid}\n`;

//   sharerData.value.link = `${env('APP_BASE_URL')}certificate/${courseDataObject.data.certificate.uuid}`;
//   sharerData.value.title = textToShare;
//   sharerData.value.visible = true;
// }

</script>

<style scoped>
:deep(.p-rating .p-rating-item .p-rating-icon) {
  color: #b4690e;
  width: 14px;
}
:deep(.p-button.p-button-secondary) {
  background: #fff;
  color: #2d2f31;
  border: 1px solid #2d2f31;
  font-weight: 700;
}
:deep(.p-button.p-button-secondary:hover) {
  background: #f7f9fa;
  border-color: #2d2f31;
}
</style>